import { CANVAS_WIDTH, CANVAS_HEIGHT } from './settings'
import { absUrl } from './abs-url'

interface Cache {
  [url: string]: ImageWrapper
}

let cache: Cache = {}

export function getImage(url: string): ImageWrapper {
  url = absUrl(url)
  if (!cache[url]) {
    cache[url] = new ImageWrapper(url)
  }
  return cache[url]
}

export class ImageWrapper {
  url: string
  loaded: boolean = false
  imgObj?: HTMLImageElement
  loadPromise?: Promise<ImageWrapper>
  imgData?: ImageData

  constructor(url: string) {
    this.url = url
  }

  load(): Promise<ImageWrapper> {
    if (!this.loadPromise) {
      this.loadPromise = new Promise((resolve, reject) => {
        const imgObj = new Image()
        imgObj.addEventListener('load', () => {
          this.loaded = true
          this.imgObj = imgObj
          resolve(this)
        })
        imgObj.addEventListener('error', () => {
          reject(new Error(`Could not load image: ${this.url}`))
        })
        imgObj.crossOrigin = 'Anonymous'
        imgObj.src = this.url
      })
    }
    return this.loadPromise
  }

  safeLoad(): Promise<boolean> {
    return this.load().then(
      () => true,
      (e: Error) => {
        console.error(
          `Error loading image '${this.url}' caught by safeLoad: ${e.message}`
        )
        return false
      }
    )
  }

  ensureLoaded(): void {
    if (!this.loaded) {
      console.warn(`Image not loaded yet: ${this.url}`)
    }
  }

  getImageObj(): HTMLImageElement | undefined {
    this.ensureLoaded()
    return this.imgObj
  }

  getImageData(): ImageData | undefined {
    this.ensureLoaded()
    if (!this.imgData) {
      // Create a temporary canvas
      const canvas = document.createElement('canvas')
      canvas.width = CANVAS_WIDTH
      canvas.height = CANVAS_HEIGHT
      const ctx = canvas.getContext('2d')
      if (ctx && this.imgObj) {
        ctx.drawImage(this.imgObj, 0, 0)
        this.imgData = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
      }
      if (typeof canvas.remove === 'function') {
        canvas.remove()
      }
    }
    return this.imgData
  }
}
